/**
 * TaskGeneratorSheet - Interface unifiée pour générer des tâches
 * Gère la génération de journée ou de semaine selon le mode
 */

import React, { memo, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Sparkles, ChevronDown, ChevronUp,
  Check, AlertCircle, Loader2, Eye, Zap, Sun, Moon
} from 'lucide-react';
import { BottomSheet } from './BottomSheet';
import weekGeneratorService from '../../services/weekGeneratorService';
import { useAdvancedSettings } from '../../hooks/useAdvancedSettings';
import { Sliders } from 'lucide-react';

const WEEK_THEMES = [
  { id: 'balance', label: 'Équilibre', emoji: '⚖️' },
  { id: 'productivity', label: 'Productivité', emoji: '🎯' },
  { id: 'health', label: 'Santé', emoji: '🌱' },
  { id: 'social', label: 'Social', emoji: '👥' },
];

// Couleurs par catégorie
const CATEGORY_COLORS = {
  vital: { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-500/30' },
  chore: { bg: 'bg-amber-500/20', text: 'text-amber-300', border: 'border-amber-500/30' },
  pleasure: { bg: 'bg-emerald-500/20', text: 'text-emerald-300', border: 'border-emerald-500/30' },
  reward: { bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-500/30' }
};

// Configuration des modes
const MODE_CONFIG = {
  week: {
    title: 'Générer ma semaine',
    buttonText: 'Générer ma semaine',
    gradient: 'from-cyan-500/20 to-purple-500/20',
    border: 'border-cyan-500/30',
    iconBg: 'bg-cyan-500/30',
    iconColor: 'text-cyan-300',
    textColor: 'text-cyan-200',
    textColorAlt: 'text-cyan-400/70',
    checkColor: 'text-cyan-400',
    selectedBg: 'bg-cyan-500/30',
    selectedBorder: 'border-cyan-500/50',
    buttonGradient: 'from-cyan-500 to-purple-500',
  },
  day: {
    title: 'Générer ma journée',
    buttonText: 'Générer cette journée',
    gradient: 'from-emerald-500/20 to-cyan-500/20',
    border: 'border-emerald-500/30',
    iconBg: 'bg-emerald-500/30',
    iconColor: 'text-emerald-300',
    textColor: 'text-emerald-200',
    textColorAlt: 'text-emerald-400/70',
    checkColor: 'text-emerald-400',
    selectedBg: 'bg-emerald-500/30',
    selectedBorder: 'border-emerald-500/50',
    buttonGradient: 'from-emerald-500 to-cyan-500',
  },
};

/**
 * TaskGeneratorSheet - Composant unifié de génération
 * @param {string} mode - 'week' ou 'day'
 * @param {boolean} isOpen - État d'ouverture
 * @param {function} onClose - Callback de fermeture
 * @param {function} onGenerate - Callback de génération
 * @param {Date|string} selectedDate - Date sélectionnée (mode day)
 * @param {Object|Array} existingTasks - Tâches existantes
 * @param {boolean} isWeekend - Si c'est un weekend (mode day)
 */
const TaskGeneratorSheet = memo(function TaskGeneratorSheet({
  mode = 'week',
  isOpen,
  onClose,
  onGenerate,
  selectedDate,
  existingTasks = mode === 'week' ? {} : [],
  isWeekend = false
}) {
  const { settings } = useAdvancedSettings();
  const config = MODE_CONFIG[mode];

  // États
  const [selectedTemplate, setSelectedTemplate] = useState(
    settings?.autoGeneration?.template || 'balanced'
  );
  const [includeWeekends, setIncludeWeekends] = useState(
    settings?.autoGeneration?.includeWeekends ?? true
  );
  const [autoAddHygiene, setAutoAddHygiene] = useState(
    settings?.autoGeneration?.autoAddHygiene ?? true
  );
  const [autoAddBreaks, setAutoAddBreaks] = useState(
    settings?.autoGeneration?.autoAddBreaks ?? true
  );
  const [showPreview, setShowPreview] = useState(mode === 'day');
  const [previewDay, setPreviewDay] = useState(isWeekend ? 'weekend' : 'weekday');
  const [isGenerating, setIsGenerating] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [intensityLevel, setIntensityLevel] = useState(3); // 1-5
  const [weekTheme, setWeekTheme] = useState('balance');
  const [editablePreview, setEditablePreview] = useState(null); // null = use auto preview

  // Templates disponibles
  const templates = useMemo(() => weekGeneratorService.getAvailableTemplates(), []);

  // Prévisualisation
  const preview = useMemo(() => {
    const isWeekendPreview = mode === 'day' ? isWeekend : previewDay === 'weekend';
    return weekGeneratorService.previewDay(selectedTemplate, isWeekendPreview);
  }, [selectedTemplate, previewDay, mode, isWeekend]);

  // Vérifier si des tâches existent
  const hasExistingTasks = useMemo(() => {
    if (mode === 'week') {
      return Object.values(existingTasks).some(tasks => tasks && tasks.length > 0);
    }
    return existingTasks && existingTasks.length > 0;
  }, [existingTasks, mode]);

  // Date formatée (mode day)
  const formattedDate = useMemo(() => {
    if (mode !== 'day' || !selectedDate) return '';
    const date = new Date(selectedDate);
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  }, [selectedDate, mode]);

  // Gérer la génération
  const handleGenerate = useCallback(async () => {
    if (hasExistingTasks && !confirmOverwrite) {
      setConfirmOverwrite(true);
      return;
    }

    setIsGenerating(true);

    try {
      if (mode === 'week') {
        // Génération de semaine
        const weekTasks = weekGeneratorService.generateWeek(new Date(), {
          template: selectedTemplate,
          includeWeekends,
          autoAddHygiene,
          autoAddBreaks,
          intensityLevel,
          weekTheme,
        });

        if (onGenerate) {
          await onGenerate(weekTasks);
        }
      } else {
        // Génération de journée
        const dateStr = selectedDate instanceof Date
          ? selectedDate.toISOString().split('T')[0]
          : selectedDate;

        const dayTasks = weekGeneratorService.generateDayTasks(dateStr, isWeekend, {
          template: selectedTemplate,
          autoAddHygiene,
          autoAddBreaks
        });

        if (onGenerate) {
          await onGenerate(dayTasks, dateStr);
        }
      }

      onClose();
    } catch (error) {
      console.error(`Erreur génération ${mode}:`, error);
    } finally {
      setIsGenerating(false);
      setConfirmOverwrite(false);
    }
  }, [mode, selectedTemplate, includeWeekends, autoAddHygiene, autoAddBreaks,
      hasExistingTasks, confirmOverwrite, onGenerate, onClose, selectedDate, isWeekend]);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={config.title}
      size="large"
    >
      <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto pb-24">

        {/* En-tête */}
        <div className={`p-3 rounded-xl bg-gradient-to-r ${config.gradient} border ${config.border}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${config.iconBg}`}>
              {mode === 'week' ? (
                <Sparkles className={`w-5 h-5 ${config.iconColor}`} />
              ) : isWeekend ? (
                <Moon className={`w-5 h-5 ${config.iconColor}`} />
              ) : (
                <Sun className={`w-5 h-5 ${config.iconColor}`} />
              )}
            </div>
            <div>
              <p className={`text-sm font-medium ${config.textColor}`}>
                {mode === 'week' ? 'Génération automatique' : formattedDate}
              </p>
              <p className={`text-xs ${config.textColorAlt}`}>
                {mode === 'week'
                  ? 'Crée une semaine de tâches adaptée à ton rythme'
                  : isWeekend ? 'Template weekend appliqué' : 'Template jour de semaine'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Sélection du template */}
        <div>
          <p className="text-[10px] font-medium uppercase text-zinc-500 mb-2 flex items-center gap-1.5">
            <Calendar className="w-3 h-3" />
            Choisir un template
          </p>
          <div className="grid grid-cols-2 gap-2">
            {templates.map(template => (
              <button
                key={template.key}
                onClick={() => setSelectedTemplate(template.key)}
                className={`p-3 rounded-xl text-left transition-all ${
                  selectedTemplate === template.key
                    ? `${config.selectedBg} border-2 ${config.selectedBorder}`
                    : 'bg-zinc-800/50 border-2 border-transparent hover:border-zinc-700'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-zinc-200">{template.name}</span>
                  {selectedTemplate === template.key && (
                    <Check className={`w-4 h-4 ${config.checkColor}`} />
                  )}
                </div>
                <p className="text-[10px] text-zinc-500">{template.description}</p>
                <p className="text-[9px] text-zinc-600 mt-1">
                  {template.taskCount} tâches{mode === 'week' ? '/jour' : ''}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Options */}
        <div className="space-y-2">
          <p className="text-[10px] font-medium uppercase text-zinc-500 mb-2">Options</p>

          {/* Option weekends (mode week uniquement) */}
          {mode === 'week' && (
            <ToggleOption
              label="Inclure les weekends"
              description="Génère aussi samedi et dimanche"
              checked={includeWeekends}
              onChange={() => setIncludeWeekends(!includeWeekends)}
            />
          )}

          <ToggleOption
            label="Ajouter les routines d'hygiène"
            description="Brossage de dents, skincare..."
            checked={autoAddHygiene}
            onChange={() => setAutoAddHygiene(!autoAddHygiene)}
          />

          {/* Option pauses (pas pour weekend en mode day) */}
          {(mode === 'week' || !isWeekend) && (
            <ToggleOption
              label="Ajouter les pauses"
              description="Pauses régulières pendant le travail"
              checked={autoAddBreaks}
              onChange={() => setAutoAddBreaks(!autoAddBreaks)}
            />
          )}
        </div>

        {/* Intensité et thème (mode week uniquement) */}
        {mode === 'week' && (
          <div className="space-y-3">
            {/* Intensity slider */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-medium uppercase text-zinc-500 flex items-center gap-1.5">
                  <Sliders className="w-3 h-3" />
                  Intensité de la semaine
                </p>
                <span className="text-xs font-bold text-zinc-300">
                  {['', 'Très légère', 'Légère', 'Modérée', 'Chargée', 'Très chargée'][intensityLevel]}
                </span>
              </div>
              <input
                type="range"
                min={1}
                max={5}
                value={intensityLevel}
                onChange={e => setIntensityLevel(Number(e.target.value))}
                className="w-full h-2 rounded-full accent-cyan-500 bg-zinc-700 cursor-pointer"
              />
              <div className="flex justify-between text-[9px] text-zinc-700 mt-0.5">
                <span>Légère</span><span>Modérée</span><span>Chargée</span>
              </div>
            </div>

            {/* Week theme */}
            <div>
              <p className="text-[10px] font-medium uppercase text-zinc-500 mb-1.5">Thème de la semaine</p>
              <div className="grid grid-cols-4 gap-1.5">
                {WEEK_THEMES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setWeekTheme(t.id)}
                    className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg text-center transition-all ${
                      weekTheme === t.id
                        ? 'bg-cyan-500/20 border border-cyan-500/50'
                        : 'bg-zinc-800/40 border border-transparent hover:border-zinc-700'
                    }`}
                  >
                    <span className="text-base">{t.emoji}</span>
                    <span className={`text-[9px] font-medium ${weekTheme === t.id ? 'text-cyan-300' : 'text-zinc-500'}`}>
                      {t.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Prévisualisation */}
        <div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="w-full flex items-center justify-between p-3 bg-zinc-800/50 rounded-xl
              hover:bg-zinc-800/70 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-zinc-400" />
              <span className="text-sm text-zinc-300">
                {mode === 'week' ? 'Prévisualiser la journée type' : 'Aperçu de la journée'}
              </span>
            </div>
            {showPreview ? (
              <ChevronUp className="w-4 h-4 text-zinc-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-zinc-500" />
            )}
          </button>

          <AnimatePresence>
            {showPreview && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-3 space-y-3">
                  {/* Toggle jour semaine / weekend (mode week uniquement) */}
                  {mode === 'week' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPreviewDay('weekday')}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 ${
                          previewDay === 'weekday'
                            ? 'bg-amber-500/30 text-amber-300 border border-amber-500/50'
                            : 'bg-zinc-800/50 text-zinc-400'
                        }`}
                      >
                        <Sun className="w-3 h-3" />
                        Jour semaine
                      </button>
                      <button
                        onClick={() => setPreviewDay('weekend')}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 ${
                          previewDay === 'weekend'
                            ? 'bg-indigo-500/30 text-indigo-300 border border-indigo-500/50'
                            : 'bg-zinc-800/50 text-zinc-400'
                        }`}
                      >
                        <Moon className="w-3 h-3" />
                        Weekend
                      </button>
                    </div>
                  )}

                  {/* Liste des tâches preview (éditable) */}
                  {(() => {
                    const displayList = editablePreview !== null ? editablePreview : preview;
                    const removeTask = (i) => {
                      const base = editablePreview !== null ? editablePreview : preview;
                      setEditablePreview(base.filter((_, idx) => idx !== i));
                    };
                    return (
                      <>
                        <div className={`space-y-1 ${mode === 'week' ? 'max-h-60' : 'max-h-48'} overflow-y-auto`}>
                          {displayList.map((task, idx) => {
                            const colors = CATEGORY_COLORS[task.category] || CATEGORY_COLORS.chore;
                            return (
                              <motion.div
                                key={idx}
                                layout
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 10 }}
                                transition={{ delay: idx * 0.01 }}
                                className={`flex items-center gap-2 p-2 rounded-lg ${colors.bg} border ${colors.border} group`}
                              >
                                <span className="text-[10px] font-mono text-zinc-400 w-10">{task.time}</span>
                                <span className="text-sm">{task.emoji}</span>
                                <span className={`flex-1 text-xs font-medium ${colors.text}`}>{task.task}</span>
                                <span className="text-[9px] text-zinc-500">{task.duration}min</span>
                                <button
                                  onClick={() => removeTask(idx)}
                                  className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-red-500/20 text-zinc-600 hover:text-red-400 transition-all"
                                  title="Supprimer cette tâche du planning"
                                >
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </motion.div>
                            );
                          })}
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-[9px] text-zinc-500">
                            {displayList.length} tâche{displayList.length > 1 ? 's' : ''} {mode === 'week' ? 'par jour' : 'prévues'}
                          </p>
                          {editablePreview !== null && (
                            <button
                              onClick={() => setEditablePreview(null)}
                              className="text-[9px] text-zinc-600 hover:text-zinc-400 transition-colors"
                            >
                              Réinitialiser
                            </button>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Confirmation si tâches existantes */}
        <AnimatePresence>
          {confirmOverwrite && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="p-4 rounded-xl bg-amber-500/20 border border-amber-500/30"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-300">
                    Des tâches existent déjà
                  </p>
                  <p className="text-xs text-amber-400/70 mt-1">
                    {mode === 'week'
                      ? 'Générer une nouvelle semaine remplacera les tâches existantes. Es-tu sûr(e) de vouloir continuer ?'
                      : 'Générer une nouvelle journée ajoutera des tâches à celles existantes. Continuer ?'
                    }
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => setConfirmOverwrite(false)}
                      className="px-3 py-1.5 bg-zinc-800 text-zinc-300 rounded-lg text-xs font-medium"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleGenerate}
                      className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium"
                    >
                      Confirmer
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bouton générer */}
        <motion.button
          onClick={handleGenerate}
          disabled={isGenerating}
          className={`w-full py-4 rounded-xl font-medium flex items-center justify-center gap-2
            transition-all ${
              isGenerating
                ? 'bg-zinc-700 text-zinc-400'
                : `bg-gradient-to-r ${config.buttonGradient} text-white hover:opacity-90`
            }`}
          whileTap={{ scale: 0.98 }}
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Génération en cours...
            </>
          ) : (
            <>
              <Zap className="w-5 h-5" />
              {config.buttonText}
            </>
          )}
        </motion.button>

        {/* Info */}
        <p className="text-[9px] text-zinc-500 text-center">
          Les tâches seront générées selon tes horaires dans les paramètres
        </p>
      </div>
    </BottomSheet>
  );
});

/**
 * Toggle option component
 */
const ToggleOption = memo(function ToggleOption({ label, description, checked, onChange }) {
  return (
    <button
      onClick={onChange}
      className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
        checked
          ? 'bg-emerald-500/20 border border-emerald-500/30'
          : 'bg-zinc-800/30 border border-zinc-700/30 hover:bg-zinc-800/50'
      }`}
    >
      <div className="text-left">
        <span className={`text-sm font-medium ${checked ? 'text-emerald-300' : 'text-zinc-300'}`}>
          {label}
        </span>
        <p className="text-[10px] text-zinc-500">{description}</p>
      </div>
      <div className={`w-10 h-5 rounded-full transition-colors flex items-center ${
        checked ? 'bg-emerald-500' : 'bg-zinc-600'
      }`}>
        <div className={`w-4 h-4 bg-white rounded-full shadow mx-0.5 transition-transform ${
          checked ? 'translate-x-5' : ''
        }`} />
      </div>
    </button>
  );
});

// Exports pour rétrocompatibilité
export const WeekGeneratorSheet = memo(function WeekGeneratorSheet(props) {
  return <TaskGeneratorSheet {...props} mode="week" />;
});

export const DayGeneratorSheet = memo(function DayGeneratorSheet(props) {
  return <TaskGeneratorSheet {...props} mode="day" />;
});

export { TaskGeneratorSheet };
export default TaskGeneratorSheet;
